<template>
  <div class="form-filter">
    <div class="form-block">
      <div class="flex some-inputs">
        <div class="flex flex-col m-r-20 input-container">
          <label class="small-label">Наименование бренда</label>
          <SceletonInput :is-loading="isLoadingData">
            <InputText
              id="slug"
              placeholder="Введите значение"
              v-model="brand.brand_name"
              :invalid="isInvalidForm && brand.brand_name == ''"
            />
          </SceletonInput>
        </div>
        <div class="flex flex-col input-container">
          <label class="small-label">Категории</label>
          <SceletonInput :is-loading="isLoadingData">
            <MultiSelect
              :loading="isLoadingDataCats"
              v-model="brand.categories"
              optionValue="id"
              :options="catsList"
              optionLabel="name"
              :invalid="isInvalidForm && brand.categories.length == 0"
              placeholder="Выберите значение"
            />
          </SceletonInput>
        </div>
      </div>

      <div class="flex flex-col fl-1 m-t-14">
        <SceletonInput :is-loading="isLoadingData">
          <div class="flex">
            <label for="use_in_wb_parser" class="m-r-20"
              >Используется в WB Парсер</label
            >
            <InputSwitch
              v-model="brand.is_used_in_wbparser"
              inputId="use_in_wb_parser"
            />
          </div>
        </SceletonInput>

        <SceletonInput :is-loading="isLoadingData">
          <div class="flex m-t-20">
            <label for="use_in_dl" class="m-r-20"
              >Используется в DataLens</label
            >
            <InputSwitch
              v-model="brand.is_used_in_datalens"
              inputId="use_in_dl"
            />
          </div>
        </SceletonInput>
      </div>
    </div>

    <div class="form-block">
      <label for="name" class="small-label">Паттерны</label>
      <div
        class="flex flex-col fl-1 m-b-8 role-container"
        v-for="(pattern, idxr) in brand.brand_patterns"
        :key="`pattern-${idxr}`"
      >
        <div class="flex flex-center">
          <InputText
            id="name"
            class="fl-1"
            placeholder="Введите значение"
            v-model="pattern.pattern"
            :invalid="isInvalidForm && pattern.pattern == ''"
          />
          <div
            class="group-button-container role flex"
            :class="[idxr != brand.brand_patterns.length - 1 ? 'hidden' : '']"
          >
            <div
              v-if="brand.brand_patterns.length > 1"
              class="delete-button flex flex-center m-l-8"
              @click="deletePattern(idxr)"
            >
              <span class="pi pi-trash f-s-12"></span>
            </div>
            <div class="add-button flex flex-center" @click="addPattern(idxr)">
              <span class="pi pi-plus"></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  useBrandsStore,
  BrandCategoryCreate,
} from "@/store/dictionaries/brands";
import { useCategoriesStore } from "@/store/catalog_data/categories";
import { CategoryInApi } from "@/store/catalog_data/categories";

import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import SceletonInput from "@/shared/forms/SceletonInput.vue";
import InputSwitch from "primevue/inputswitch";

import {
  areBrandFieldsValid,
  createBrandPk,
} from "@/services/dictionaries/brands";
import { rootGetters } from "@/main";

const emit = defineEmits(["setLoading"]);
const route = useRoute();
const router = useRouter();
const store = useBrandsStore();
const categoriesStore = useCategoriesStore();
const catsList = ref([]);

const brand = ref({
  brand_name: "",
  brand_pk: "",
  categories: [] as number[],
  brand_patterns: [{ pattern: "" }],
  is_used_in_wbparser: false,
  is_used_in_datalens: false,
});

const isLoadingData = ref(false);
const isLoadingDataCats = ref(false);
const isInvalidForm = ref(false);
const isBrandSetted = computed(() => {
  return Boolean(route.params.id);
});

const reload = () => {
  isLoadingDataCats.value = true;
  categoriesStore
    .getC1Categories()
    .then((cats) => {
      catsList.value = cats;
    })
    .finally(() => {
      isLoadingDataCats.value = false;
    });
  if (isBrandSetted.value) {
    isLoadingData.value = true;
    const brandPk = route.params.id.toString();
    store
      .getBrand(brandPk)
      .then((brandClone) => {
        if (brandClone) {
          brand.value.brand_name = brandClone.brand_name;
          brand.value.brand_patterns = brandClone.brand_patterns;
          brand.value.brand_pk = brandClone.brand_pk;
          brand.value.categories = brandClone.categories.map(
            (cat: any) => cat.id
          );
          brand.value.is_used_in_wbparser =
            brandClone.is_used_in_wbparser != null
              ? brandClone.is_used_in_wbparser
              : false;
          brand.value.is_used_in_datalens =
            brandClone.is_used_in_datalens != null
              ? brandClone.is_used_in_datalens
              : false;
        }
      })
      .finally(() => {
        isLoadingData.value = false;
      });
  }
};

onMounted(() => {
  reload();
});

const createBrand = () => {
  const categories: BrandCategoryCreate[] = brand.value.categories.map(
    (category) => ({
      category_id: category,
    })
  );

  const brandToServer = {
    brand_patterns: brand.value.brand_patterns,
    brand: {
      brand_name: brand.value.brand_name,
      brand_pk: brand.value.brand_pk
        ? brand.value.brand_pk
        : createBrandPk(brand.value.brand_name),
      is_used_in_wbparser: brand.value.is_used_in_wbparser,
      is_used_in_datalens: brand.value.is_used_in_datalens,
    },
    categories: categories,
  };

  if (!areBrandFieldsValid(brandToServer)) {
    isInvalidForm.value = true;
    rootGetters.toast.show(
      "warn",
      "Ошибка сохранения",
      `Проверьте, что все необходимые поля заполнены.`
    );
    return;
  }

  emit("setLoading", true);
  if (!isBrandSetted.value) {
    performAction(() => store.createBrand(brandToServer));
  } else {
    performAction(() =>
      store.updateBrand(brandToServer, route.params.id.toString())
    );
  }
};

const performAction = (action: any) => {
  const handleCampaignSuccess = () => {
    router.push({ name: "brands" });
    setTimeout(() => {
      store.activeFirst = false;
    }, 5000);
  };

  action()
    .then(handleCampaignSuccess)
    .catch((err: any) => {
      if (err.response && err.response.status == 409)
        rootGetters.toast.show("warn", "Ошибка", err.response.data.detail);
      if (err.response && err.response.status == 403)
        rootGetters.toast.show("warn", "Ошибка", "Нет доступа.");
    })
    .finally(() => {
      emit("setLoading", false);
    });
};

const addPattern = (idx: number) => {
  brand.value.brand_patterns.splice(idx + 1, 0, { pattern: "" });
};

const deletePattern = (idx: number) => {
  brand.value.brand_patterns.splice(idx, 1);
};

defineExpose({
  createBrand,
});
</script>

<style lang="scss" scoped>
.form-filter {
  max-width: 100%;
  width: 100%;
  padding-bottom: 40px;
  min-width: 500px;
}

.form-block-title {
  margin-bottom: 8px;
}

.role-container:hover .group-button-container.hidden.role {
  display: flex;
}

.small-label {
  color: #a19cab;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.43rem;
  padding-bottom: 4px;
  padding-left: 4px;
}

.group-button-container.role.hidden {
  display: none;
}

.delete-button {
  border-radius: 5px;
  background: #f7f7fd;
  width: 32px;
  height: 32px;
  padding-left: 9px;
  cursor: pointer;
  margin-bottom: 2px;

  &:hover {
    background-color: #fafaff;
  }
}

.add-button {
  background-color: #dbdaf9;
  border-radius: 5px;
  padding: 3px 9px;
  cursor: pointer;
  height: 32px;
  margin-left: 12px;
  margin-bottom: 1px;

  &:hover {
    background-color: #d4d3f8;
    outline-color: transparent;
  }
}

.f-s-12 {
  font-size: 12px;
}

.sub-block {
  padding: 12px;
  background-color: #f7f7fd;
  border: 1px solid #d0d0d6;
  border-radius: 6px;
}

.some-inputs {
  max-width: 100%;
}

.input-container {
  max-width: calc(50% - 4px);
  width: calc(50% - 4px);
}
</style>
